import type {
  Id,
  OrderJourney,
  OrderStageChange,
  OrderStageSettings,
  StageOverrideOutcome,
  StageRule,
  StageRuleInput,
  StageTagFailure,
  StageTagSettings,
  StagesRepository,
  UnruledTag,
  WriteResult,
} from "@estelle/domain";

import { ForbiddenError, apiFetch } from "@/adapters/api/base";

/**
 * Order stage settings and history, over HTTP (#465).
 *
 * `apps/api/src/routes/stages.ts` is the other half. An order's stage itself
 * arrives on the order row; this carries the translation table, the overdue
 * limit and the history. A refusal the server gives for an expected reason is a
 * `WriteResult` body; a 403 is `forbidden` on a write and `ForbiddenError` on a
 * read; only a transport failure throws.
 */

const BASE = "/api/stages";

async function read<T>(path: string): Promise<T> {
  const response = await apiFetch(`${BASE}${path}`);
  if (response.status === 403) throw new ForbiddenError(`${BASE}${path}`);
  if (!response.ok) {
    throw new Error(
      `Stage request failed: ${String(response.status)} on ${path}`,
    );
  }
  return (await response.json()) as T;
}

async function write<T>(
  path: string,
  init: RequestInit,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, init);
  const result = (await response.json().catch(() => null)) as
    WriteResult<T> | { permission?: unknown } | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  if (response.status === 403) {
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to do that.",
        permission:
          path === "/override"
            ? "orders.stage.override"
            : "settings.tags.manage",
      },
    };
  }
  throw new Error(
    `Stage ${init.method ?? "GET"} failed: ${String(response.status)} on ${path}`,
  );
}

const json = (method: "POST" | "PUT", body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

export function createApiStages(): StagesRepository {
  return {
    listRules: async () => (await read<{ items: StageRule[] }>("/rules")).items,
    createRule: (input: StageRuleInput) =>
      write<StageRule>("/rules", json("POST", input)),
    updateRule: (id: Id, input: StageRuleInput) =>
      write<StageRule>(`/rules/${encodeURIComponent(id)}`, json("PUT", input)),
    deleteRule: (id: Id) =>
      write<{ id: Id }>(`/rules/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    unruledTags: async () =>
      (await read<{ items: UnruledTag[] }>("/unruled-tags")).items,
    getTagSettings: () => read<StageTagSettings>("/tag-settings"),
    saveTagSettings: (enabled: boolean) =>
      write<StageTagSettings>("/tag-settings", json("PUT", { enabled })),
    tagFailures: async () =>
      (await read<{ items: StageTagFailure[] }>("/tag-failures")).items,
    retryTags: () =>
      write<{ tagged: number; failed: number }>("/tag-sync", {
        method: "POST",
      }),
    getSettings: () => read<OrderStageSettings>("/settings"),
    saveSettings: (input) =>
      write<OrderStageSettings>("/settings", json("PUT", input)),
    setStageOverride: (orderIds, input) =>
      write<StageOverrideOutcome>(
        "/override",
        json("POST", { orderIds: [...orderIds], ...input }),
      ),
    history: async (orderId: Id): Promise<OrderStageChange[]> =>
      (
        await read<{ items: OrderStageChange[] }>(
          `/orders/${encodeURIComponent(orderId)}/history`,
        )
      ).items,
    journey: (orderId: Id): Promise<OrderJourney> =>
      read<OrderJourney>(`/orders/${encodeURIComponent(orderId)}/journey`),
  };
}
